import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FlexBox, FlexBoxDirection, Input, Label, WrappingType } from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useDitaQueryStore } from 'stores/CreateDitaQueryStore';
import { validateString } from 'utils/validator';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { IssueType, useCreateDitaMutation } from 'generated/graphql';
import { commonDitaQueryInitialStateFields } from 'stores/slices/DitaQuerySlices/CommonDitaQuerySlice';
import isMatch from 'lodash/isMatch';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

export const DitaQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const store = useDitaQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateDitaMutation();

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return validateAllFields();
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  const validateTitle = () => {
    return validateString(store.fileTitle, store.setFileTitleValueState);
  };

  const validateFileName = () => {
    return validateString(store.fileName, store.setFileNameValueState);
  };

  const validateLoio = () => {
    return validateString(store.loio, store.setLoioValueState);
  };

  const validateDeliverable = () => {
    return validateString(store.deliverable, store.setDeliverableValueState);
  };

  const validateCmsProject = () => {
    return validateString(store.cmsProject, store.setCmsProjectValueState);
  };

  const validateSourceContact = () => {
    return validateString(store.sourceContact, store.setSourceContactValueState);
  };

  const validateAllFields = () => {
    const isTtileValid = validateTitle();
    const isFileNameValid = validateFileName();
    const isLoioValid = validateLoio();
    const isDeliverableValid = validateDeliverable();
    const isCmsProjectValid = validateCmsProject();
    const isSourceContactValid = validateSourceContact();

    return (
      isTtileValid &&
      isFileNameValid &&
      isLoioValid &&
      isDeliverableValid &&
      isCmsProjectValid &&
      isSourceContactValid
    );
  };

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProject,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        fileTitle: store.fileTitle,
        fileName: store.fileName,
        loio: store.loio,
        deliverable: store.deliverable,
        cmsProject: store.cmsProject,
        submap: store.submap,
        xtmProjectName: store.xtmProjectName,
        sourceContact: store.sourceContact,
        languagesTranslatorTeams: store.languagesTranslatorTeams
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, commonDitaQueryInitialStateFields);
  };

  const onChangeXtmProjectName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setXtmProjectName(value);
  };

  const onChangeFileTitle = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setFileTitle(value);
    validateString(value, store.setFileTitleValueState);
  };

  const onChangeFileName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setFileName(value);
    validateString(value, store.setFileNameValueState);
  };

  const onChangeSubmap = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setSubmap(value);
  };

  const onChangeLoio = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setLoio(value);
    validateString(value, store.setLoioValueState);
  };

  const onChangeDeliverable = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setDeliverable(value);
    validateString(value, store.setDeliverableValueState);
  };

  const onChangeCmsProject = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setCmsProject(value);
    validateString(value, store.setCmsProjectValueState);
  };

  const onChangeSourceContact = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setSourceContact(value);
    validateString(value, store.setSourceContactValueState);
  };

  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="xtm-project-label" className={classes.label} showColon>
            <span data-help-id="create-query-dita-xtm-project">{t('xtm-project-label')}</span>
          </Label>
          <Input
            aria-labelledby="xtm-project-label"
            onChange={onChangeXtmProjectName}
            className={classes.formLineInput}
            value={store.xtmProjectName}
            disabled={freezeStore.xtmProjectName}
          ></Input>
        </div>
        <div className={classes.div}>
          <Label id="file-title-label" className={classes.label} showColon required>
            <span data-help-id="create-query-dita-title">{t('file-title-label')}</span>
          </Label>
          <Input
            aria-labelledby="file-title-label"
            valueState={store.fileTitleValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-title')}
              </Label>
            }
            onChange={onChangeFileTitle}
            className={classes.formLineInput}
            value={store.fileTitle}
            disabled={freezeStore.fileTitle}
          ></Input>
        </div>
        <div className={classes.div}>
          <Label id="file-name-label" className={classes.label} showColon required>
            <span data-help-id="create-query-dita-file-name">{t('file-name-label')}</span>
          </Label>
          <Input
            aria-labelledby="file-name-label"
            valueState={store.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
            className={classes.formLineInput}
            value={store.fileName}
            disabled={freezeStore.fileName}
          ></Input>
        </div>
        <div className={classes.div}>
          <Label id="submap-label" className={classes.label} showColon>
            <span data-help-id="create-query-dita-submap">{t('submap-label')}</span>
          </Label>
          <Input
            aria-labelledby="submap-label"
            onChange={onChangeSubmap}
            className={classes.formLineInput}
            value={store.submap}
            disabled={freezeStore.submap}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="deliverable-label" className={classes.label} showColon required>
            <span data-help-id="create-query-deliverable">{t('deliverable-label')}</span>
          </Label>
          <Input
            aria-labelledby="deliverable-label"
            valueState={store.deliverableValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-deliverable')}
              </Label>
            }
            onChange={onChangeDeliverable}
            className={classes.formInput}
            value={store.deliverable}
            disabled={freezeStore.deliverable}
          />
          <Label id="cms-project-label" className={classes.secondColumnLabel} showColon required>
            <span data-help-id="create-query-cms-project">{t('cms-project-label')}</span>
          </Label>
          <Input
            aria-labelledby="cms-project-label"
            valueState={store.cmsProjectValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-cms-project')}
              </Label>
            }
            onChange={onChangeCmsProject}
            className={classes.formInput}
            value={store.cmsProject}
            disabled={freezeStore.cmsProject}
          />
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="loio-label" className={classes.label} showColon required>
            <span data-help-id="create-query-dita-loio">{t('loio-label')}</span>
          </Label>
          <Input
            aria-labelledby="loio-label"
            valueState={store.loioValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-loio')}
              </Label>
            }
            onChange={onChangeLoio}
            className={classes.formInput}
            value={store.loio}
            disabled={freezeStore.loio}
          ></Input>
          <Label id="source-contact-label" className={classes.secondColumnLabel} showColon required>
            <span data-help-id="create-query-dita-source-contact">
              {t('dita-source-contact-label')}
            </span>
          </Label>
          <Input
            aria-labelledby="source-contact-label"
            className={classes.formInput}
            onChange={onChangeSourceContact}
            valueState={store.sourceContactValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-source-contact')}
              </Label>
            }
            value={store.sourceContact}
            disabled={freezeStore.sourceContact}
          />
        </div>
      </FlexBox>
    </>
  );
});
